import React, { useEffect, useState } from 'react';
/**
 * SentenceErrorJudgeRenderer — Pick which sentence contains a spelling error.
 *
 * The student sees 4 sentences and taps the one with the error.
 * Data format: data.sentences = [{ text, hasError, correction?, explanation? }]
 */
import { QuestionRendererProps } from './types';
import { motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon } from 'lucide-react';
import { hexToRgba, FALLBACK_COLOR, type TailwindCSSProperties } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
interface SentenceItem {
  text: string;
  hasError: boolean;
  correction?: string;
  explanation?: string;
}
export function SentenceErrorJudgeRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data || {};
  const sentences: SentenceItem[] = config.sentences as SentenceItem[] || [];
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  useEffect(() => {
    setSelectedIndex(null);
  }, [question.id]);
  useEffect(() => {
    onReadyChange?.(selectedIndex !== null);
  }, [selectedIndex, onReadyChange]);
  useEdgeTriggeredSubmit(selectedIndex ?? -1, shouldValidate, onAnswer, question.id);
  const handleSelect = (index: number) => {
    if (shouldValidate) return;
    // Toggle: clicking the active sentence deselects it.
    setSelectedIndex(prev => prev === index ? null : index);
  };
  return <div className="space-y-3" data-id="element-11828">
      <p className="text-sm text-gray-500 flex items-center gap-1.5 mb-4" data-id="element-11829">
        <AlertCircleIcon className="w-4 h-4" data-id="element-11830" />
        Klik op de zin met de fout
      </p>
      {sentences.map((sentence, index) => {
      const isSelected = selectedIndex === index;
      return <motion.button key={index} type="button" onClick={() => handleSelect(index)} whileTap={{
        scale: 0.98
      }} className={`
              w-full text-left p-4 rounded-xl border-2 transition-all duration-150
              ${isSelected ? 'shadow-sm' : 'border-gray-200 bg-white hover:bg-gray-50'}
              ${shouldValidate ? 'pointer-events-none' : 'cursor-pointer'}
            `} style={isSelected ? {
        borderColor: color,
        backgroundColor: hexToRgba(color, 0.05),
        '--tw-ring-color': hexToRgba(color, 0.2),
        '--tw-ring-offset-width': '0px',
        '--tw-ring-offset-color': '#fff',
        '--tw-ring-offset-shadow': 'var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)',
        '--tw-ring-shadow': 'var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color)',
        boxShadow: 'var(--tw-ring-offset-shadow), var(--tw-ring-shadow), 0 1px 2px 0 rgba(0, 0, 0, 0.05)'
      } as TailwindCSSProperties : undefined} onMouseEnter={e => {
        if (!isSelected && !shouldValidate) {
          e.currentTarget.style.borderColor = hexToRgba(color, 0.3);
        }
      }} onMouseLeave={e => {
        if (!isSelected && !shouldValidate) {
          e.currentTarget.style.borderColor = '';
        }
      }} data-id="element-11831">
            <div className="flex items-start gap-3" data-id="element-11832">
              <span className={`
                flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold mt-0.5
                ${isSelected ? 'text-white' : 'bg-gray-100 text-gray-500'}
              `} style={isSelected ? {
            backgroundColor: color
          } : undefined} data-id="element-11833">
                {index + 1}
              </span>
              <span className={`text-base leading-relaxed ${isSelected ? 'font-medium' : 'text-gray-800'}`} style={isSelected ? {
            color: color
          } : undefined} data-id="element-11834">
                {sentence.text}
              </span>
              {isSelected && <CheckCircle2Icon className="w-5 h-5 flex-shrink-0 mt-0.5 ml-auto" style={{
            color: color
          }} data-id="element-11835" />}
            </div>
          </motion.button>;
    })}
    </div>;
}